/**
 * =============================================================================
 * useWlAddonKpis
 *
 * Hook TanStack Query que consome a RPC `wl_get_addon_kpis`. Alimenta a aba
 * Add-ons do painel WL Financeiro (KPIs, agrupamento por add-on e séries
 * evolutivas de 12 meses). Mesma fonte de verdade da listagem.
 *
 * @version 1.0.0
 * RELEASE: 2026-07-22 v1.0.0 - Versão inicial (Fase 5 — refatoração aba Add-ons).
 * =============================================================================
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/** Item KPI padrão (valor + delta% + opcional count/mrr). */
export interface WlAddonKpiValue {
  value: number;
  delta_pct?: number | null;
  /** Presente em KPIs de contratação mensal (novos/cancelados). */
  count?: number;
  /** Presente em total_addons (MRR total de add-ons ativos). */
  mrr?: number;
}

/** Agrupamento por add-on (top 20). */
export interface WlAddonByAddonRow {
  addon_id: string;
  addon_name: string;
  addon_slug: string;
  quantity: number;
  mrr: number;
}

/** Ponto mensal da série de receita. */
export interface WlAddonRevenuePoint {
  month: string;
  mrr: number;
  new_mrr: number;
  churn_mrr: number;
  /** Aliases compatíveis com WlRevenueTrendChart (opcionais). */
  mrr_plans?: number;
  mrr_addons?: number;
}

/** Ponto mensal da série de contagens (novos vs. cancelados). */
export interface WlAddonCountsPoint {
  month: string;
  new_addons: number;
  canceled_addons: number;
  active_total: number;
  /** Aliases compatíveis com WlTenantsTrendChart (mapeados no wrapper). */
  new_tenants?: number;
  churned_tenants?: number;
}

/** Payload completo devolvido pela RPC. */
export interface WlAddonKpisPayload {
  reference_month: string;
  timezone: string;
  kpis: {
    total_addons: WlAddonKpiValue;
    total_addons_mrr: WlAddonKpiValue;
    new_addons_month: WlAddonKpiValue;
    canceled_addons_month: WlAddonKpiValue;
    avg_addon_ticket: WlAddonKpiValue;
  };
  by_addon: WlAddonByAddonRow[];
  trend_revenue: WlAddonRevenuePoint[];
  trend_counts: WlAddonCountsPoint[];
}

/**
 * Consome `wl_get_addon_kpis` — mesma janela SP TZ dos demais KPIs financeiros.
 *
 * @param referenceMonth Mês de referência (yyyy-MM-dd). Quando null usa o mês corrente.
 * @param timezone Timezone das agregações. Default America/Sao_Paulo.
 * @param includeCourtesy Se true, inclui tenants cortesia (default false).
 */
export function useWlAddonKpis(
  referenceMonth?: string | null,
  timezone: string = 'America/Sao_Paulo',
  includeCourtesy: boolean = false,
) {
  return useQuery({
    queryKey: ['wl-addon-kpis', referenceMonth ?? 'current', timezone, includeCourtesy],
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<WlAddonKpisPayload> => {
      const { data, error } = await supabase.rpc('wl_get_addon_kpis', {
        p_reference_month: referenceMonth ?? null,
        p_timezone: timezone,
        p_months_back: 12,
        p_include_courtesy: includeCourtesy,
      } as never);
      if (error) throw error;
      return data as unknown as WlAddonKpisPayload;
    },
  });
}
